import { auth } from '@app/auth';
import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';

export const getDashboardData = createServerFn({ method: 'GET' }).handler(async () => {
  const request = getRequest();
  const session = await auth.api.getSession({ headers: request.headers });
  if (!session) throw new Error('Unauthorized');

  // Defense in depth:
  // - Enhanced client (project rule: never raw prisma when user is known)
  //   so ZenStack policies gate the queries at the engine layer.
  // - Explicit athleteId/userId filters so an org admin/agent — who ALSO
  //   has @@allow('read') on the same models — sees only their *personal*
  //   athlete dashboard, not aggregates across every athlete in their org.
  //   The dashboard is the athlete's individual stats view, not an org
  //   admin console.
  const { getEnhancedPrisma } = await import('@app/db');
  const db = getEnhancedPrisma({
    id: session.user.id,
    email: session.user.email,
    role: session.user.role || 'user',
  });
  const userId = session.user.id;

  const [totalDeals, activeDeals, totalEarnings, pendingPayments, upcomingDeadlines, recentDeals] =
    await Promise.all([
      db.deal.count({ where: { athleteId: userId } }),
      db.deal.count({ where: { athleteId: userId, status: 'ACTIVE' } }),
      db.payment.aggregate({
        where: { userId, status: 'RECEIVED' },
        _sum: { amount: true },
      }),
      db.payment.aggregate({
        where: { userId, status: { in: ['PENDING', 'OVERDUE'] } },
        _sum: { amount: true },
      }),
      db.deliverable.findMany({
        where: {
          deal: { athleteId: userId },
          status: { in: ['PENDING', 'IN_PROGRESS'] },
          dueDate: { gte: new Date() },
        },
        orderBy: { dueDate: 'asc' },
        take: 5,
        include: { deal: { select: { id: true, title: true, brandName: true } } },
      }),
      db.deal.findMany({
        where: { athleteId: userId },
        orderBy: { createdAt: 'desc' },
        take: 5,
        select: {
          id: true,
          title: true,
          brandName: true,
          status: true,
          amount: true,
          createdAt: true,
        },
      }),
    ]);

  return {
    totalDeals,
    activeDeals,
    totalEarnings: Number(totalEarnings._sum.amount) || 0,
    pendingPayments: Number(pendingPayments._sum.amount) || 0,
    // Match the slim shape emitted by `deal.dashboard` exactly so that
    // setQueryData against that query key typechecks. US-030 adds extra
    // Deliverable fields (metadata, proofs, skipProofReason) that don't
    // belong in this rollup.
    upcomingDeadlines: upcomingDeadlines.map((d) => ({
      id: d.id,
      title: d.title,
      dueDate: d.dueDate?.toISOString() ?? null,
      dealTitle: d.deal.title,
      brandName: d.deal.brandName,
      status: d.status,
    })),
    recentDeals: recentDeals.map((d) => ({
      ...d,
      amount: Number(d.amount),
      createdAt: d.createdAt.toISOString(),
    })),
  };
});
